import { RotateCw, X } from 'lucide-react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';

import { VISIT_TYPES } from '../../constants';
import {
  dayName,
  daysUntil,
  getAppointmentReadinessStatus,
  getPatient,
  getPostVisitCardStatus,
  getPostVisitTaskDaysRolling,
  isTaskCanceledForReadiness,
  isTaskReadinessComplete,
  monthDay,
} from '../../helpers';
import styles from './AppointmentCard.module.scss';

const getTaskReadinessStatus = (task) => {
  if (isTaskCanceledForReadiness(task)) return 'canceled';
  if (isTaskReadinessComplete(task)) return 'green';
  if (task?.priority === 'critical') return 'red';
  return 'yellow';
};

const getOverallCardStatus = (tasks = []) => {
  const status = getAppointmentReadinessStatus(tasks);
  return status === 'default' ? 'neutral' : status;
};

const READINESS_STATUS_ORDER = {
  green: 0,
  canceled: 1,
  yellow: 2,
  red: 3,
};

const formatAppointmentDateLabel = (date) => {
  if (!date) return '';

  return `${dayName(date)} ${monthDay(date)}`.trim();
};

const getBrowserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const getTimeZoneLabel = (date, time, timeZone = getBrowserTimeZone()) => {
  if (!timeZone) return '';

  const normalizedTime = `${time || ''}`.trim();
  const normalizedDate = `${date || ''}`.trim();
  const fallbackLabel = timeZone.split('/').pop()?.replace(/_/g, ' ') || timeZone;

  if (!normalizedDate) {
    return fallbackLabel;
  }

  const zonedDateTime = moment.tz(
    `${normalizedDate} ${normalizedTime || '00:00'}`,
    ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD'],
    timeZone,
  );

  if (zonedDateTime.isValid()) {
    const abbreviation = zonedDateTime.format('z');
    if (abbreviation && !abbreviation.startsWith('+') && abbreviation !== 'UTC') {
      return abbreviation;
    }
  }

  return fallbackLabel;
};

const AppointmentCard = ({
  appt,
  onClick,
  isSelected,
  appointmentTasks = [],
  isPostVisit = false,
  onDismiss,
  departmentLabelById = {},
}) => {
  const patient = appt?.patient ?? getPatient(appt?.patientId);
  const visitType = VISIT_TYPES[appt.visitType];
  const workflowStageLabel = `${appt?.workflowStageLabel || ''}`.trim();
  const departmentLabel = departmentLabelById[`${appt?.departmentAthenaId ?? ''}`] || '';
  const clinicDate = appt?.departmentLocalDate || appt?.date;
  const days = clinicDate ? daysUntil(clinicDate, appt?.departmentTimezone) : null;
  const hasValidDays = Number.isFinite(days);
  const activeAppointmentTasks = appointmentTasks.filter(
    (task) => !isTaskCanceledForReadiness(task),
  );
  const totalTaskCount = appointmentTasks.length;
  const completedTaskCount = appointmentTasks.filter((task) =>
    isTaskReadinessComplete(task),
  ).length;
  const outstandingTaskCount = totalTaskCount - completedTaskCount;
  const overallCardStatus = isPostVisit
    ? getPostVisitCardStatus(activeAppointmentTasks)
    : getOverallCardStatus(appointmentTasks);
  const rollingTasks = activeAppointmentTasks.filter((task) => !isTaskReadinessComplete(task));
  const rollingTaskCount = rollingTasks.length;
  const oldestRollingDays = rollingTaskCount
    ? Math.max(...rollingTasks.map(getPostVisitTaskDaysRolling))
    : 0;
  const followUpTotalCount = activeAppointmentTasks.length;
  const followUpCompletedCount = followUpTotalCount - rollingTaskCount;
  const followUpProgressPct =
    followUpTotalCount > 0 ? (followUpCompletedCount / followUpTotalCount) * 100 : 0;
  const readinessBlocks = appointmentTasks
    .map((task) => ({
      id: task.id,
      status: getTaskReadinessStatus(task),
    }))
    .sort((a, b) => READINESS_STATUS_ORDER[a.status] - READINESS_STATUS_ORDER[b.status]);
  const dateLabel = hasValidDays
    ? days === 0
      ? 'Today'
      : days === 1
        ? 'Tomorrow'
        : days < 0
          ? `${Math.abs(days)}d ago`
          : `in ${days}d`
    : '—';
  const isSoon = hasValidDays && days <= 2;
  const localTimeZoneLabel = getTimeZoneLabel(appt?.date, appt?.time);
  const departmentDate = `${appt?.departmentLocalDate || ''}`.trim();
  const hasDepartmentTime = `${appt?.departmentLocalTime || ''}`.trim().length > 0;
  const departmentTimeZoneLabel = getTimeZoneLabel(
    appt?.date,
    appt?.departmentLocalTime,
    appt?.departmentTimezone,
  );
  const shouldShowDepartmentDate = Boolean(
    departmentDate && `${appt?.date || ''}`.trim() && departmentDate !== `${appt.date}`.trim(),
  );
  const departmentDateLabel = shouldShowDepartmentDate
    ? formatAppointmentDateLabel(departmentDate)
    : '';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${styles.card} ${isSelected ? styles.selected : ''}`}
      style={{
        '--card-border':
          overallCardStatus === 'green'
            ? '#bbf7d0'
            : overallCardStatus === 'yellow'
              ? '#fde68a'
              : overallCardStatus === 'red'
                ? '#fecaca'
                : '#e4e4e7',
        '--card-left':
          overallCardStatus === 'green'
            ? '#22c55e'
            : overallCardStatus === 'yellow'
              ? '#eab308'
              : overallCardStatus === 'red'
                ? '#ef4444'
                : '#d4d4d8',
      }}
    >
      {isPostVisit && onDismiss ? (
        <span
          role="button"
          tabIndex={0}
          className={styles.dismissButton}
          onClick={(event) => {
            event.stopPropagation();
            onDismiss(appt?.id);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            onDismiss(appt?.id);
          }}
          aria-label={`Hide ${patient?.name || 'this'} post-visit follow-up`}
          title="Hide from post-visit view"
        >
          <X size={14} />
        </span>
      ) : null}
      <div className={styles.header}>
        <div>
          <div className={styles.patientName}>{patient?.name}</div>
          <div className={styles.visitMeta}>
            <span>
              {workflowStageLabel
                ? `${workflowStageLabel} · ${visitType?.short || appt.visitType}`
                : `Upcoming ${visitType?.short || appt.visitType} visit`}
            </span>
            {departmentLabel ? (
              <>
                <span className={styles.metaDivider}>·</span>
                <span className={styles.department}>{departmentLabel}</span>
              </>
            ) : null}
          </div>
        </div>
        <div className={styles.dateBlock}>
          <div className={`${styles.dateLabel} ${isSoon ? styles.dateSoon : styles.dateNormal}`}>
            {dateLabel}
          </div>
          <div className={styles.timeGroup}>
            {hasDepartmentTime ? (
              <>
                <div className={styles.timeLabel}>
                  <span className={styles.timeCaption}>Clinic</span>
                  <span>{appt.departmentLocalTime}</span>
                  {departmentTimeZoneLabel ? <span>{departmentTimeZoneLabel}</span> : null}
                </div>
                {departmentDateLabel ? (
                  <div className={styles.timeSubLabel}>
                    <span className={styles.timeCaption}>Date</span>
                    <span>{departmentDateLabel}</span>
                  </div>
                ) : null}
              </>
            ) : null}
            <div className={styles.timeLabel}>
              <span className={styles.timeCaption}>Local</span>
              <span>{appt.time}</span>
              {localTimeZoneLabel ? <span>{localTimeZoneLabel}</span> : null}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        {isPostVisit ? (
          <div className={styles.followUpRow}>
            <span className={styles.readinessLabel}>Follow-up</span>
            <div className={styles.followUpBar}>
              <div
                className={`${styles.followUpBarFill} ${styles[`followUpBarFill${overallCardStatus}`]}`}
                style={{ width: `${followUpProgressPct}%` }}
              />
            </div>
            <span
              className={styles.readinessCount}
            >{`${followUpCompletedCount}/${followUpTotalCount} done`}</span>
          </div>
        ) : (
          <div className={styles.readinessRow}>
            <span className={styles.readinessLabel}>Readiness</span>
            <div className={styles.readinessBlocks}>
              {readinessBlocks.map((block) => (
                <span
                  key={block.id}
                  className={`${styles.readinessBlock} ${styles[`readinessBlock${block.status}`]}`}
                />
              ))}
            </div>
            <span
              className={styles.readinessCount}
            >{`${completedTaskCount}/${totalTaskCount}`}</span>
          </div>
        )}
        {isPostVisit ? (
          rollingTaskCount > 0 ? (
            <div className={`${styles.rollingPill} ${styles[`rollingPill${overallCardStatus}`]}`}>
              <RotateCw size={11} className={styles.rollingIcon} aria-hidden="true" />
              {rollingTaskCount} rolling
              {oldestRollingDays > 0 ? ` · oldest ${oldestRollingDays}d` : ''}
            </div>
          ) : followUpTotalCount > 0 ? (
            <div className={styles.taskPillDone}>Ready</div>
          ) : (
            <div className={styles.taskPillMuted}>No linked tasks</div>
          )
        ) : outstandingTaskCount > 0 && overallCardStatus !== 'green' ? (
          <div className={styles.taskPill}>
            {outstandingTaskCount} outstanding task{outstandingTaskCount === 1 ? '' : 's'}
          </div>
        ) : totalTaskCount > 0 ? (
          <div className={styles.taskPillDone}>Ready</div>
        ) : (
          <div className={styles.taskPillMuted}>No linked tasks</div>
        )}
      </div>
    </button>
  );
};

AppointmentCard.propTypes = {
  appt: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    patient: PropTypes.shape({
      name: PropTypes.string,
    }),
    patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    visitType: PropTypes.string,
    workflowStageLabel: PropTypes.string,
    date: PropTypes.string,
    time: PropTypes.string,
    departmentLocalDate: PropTypes.string,
    departmentLocalTime: PropTypes.string,
    departmentTimezone: PropTypes.string,
    departmentAthenaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onClick: PropTypes.func,
  isSelected: PropTypes.bool,
  appointmentTasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.number,
      priority: PropTypes.string,
    }),
  ),
  isPostVisit: PropTypes.bool,
  onDismiss: PropTypes.func,
  departmentLabelById: PropTypes.object,
};

export default AppointmentCard;
